/** @jsxImportSource @emotion/react */
import { Button, Grid, Typography, InputAdornment, IconButton, useMediaQuery, useTheme } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StyledTextField } from '../../Components/CustomTextfield';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Il8nContext } from '../../Helper/i18n';
import { addStageData } from '../../Helper/Redux/reducers/upDown';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useEffect } from 'react';

function SignUp(props) {
    const theme = useTheme()
    const store = useSelector(state => state.storeState.stage)
    const dispatch = useDispatch()
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
    const localeText = useContext(Il8nContext)

    useEffect(() => {
        if (props.urlParams.get("email")) {
            formik.setFieldValue("email", window.atob(props.urlParams.get("email")))
        }
        if (props.urlParams.get("name")) {
            formik.setFieldValue("name", window.atob(props.urlParams.get("name")))
        }
    }, [])


    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = () => setShowPassword(!showPassword);
    const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);
    const handleMouseDownConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

    const handleNameField = (e) => {
        dispatch(addStageData({ ...store, name: e.currentTarget.value }))
    }

    const handleEmailIDField = (e) => {
        dispatch(addStageData({ ...store, email: e.currentTarget.value }))
    }

    const handleCreatePasswordField = (e) => {
        dispatch(addStageData({ ...store, password: e.currentTarget.value }))
    }

    const handleConfirmPasswordField = (e) => {
        if (store.password == e.currentTarget.value) {
            dispatch(addStageData({ ...store, password: e.currentTarget.value }))
            if (store?.error?.find(ele => ele == "confirmPassword")) {
                let errors = [...store.error]
                errors = errors.filter(ele => ele != "confirmPassword")
                dispatch(addStageData({ ...store, password: e.currentTarget.value, error: errors }))
            }
        }
        else {
            !(store?.error?.find(ele => ele == "confirmPassword")) && dispatch(addStageData({ ...store, error: store.error ? [...store.error, "confirmPassword"] : ["confirmPassword"] }))
        }
    }

    const validationSchema = yup.object({
        name: yup
            .string('')
            .matches(/^[a-zA-Z]+(?: [a-zA-Z]+)*$/, localeText?.signUpEnterValidName)
            .required(localeText?.signUpNameRequired),
        email: yup
            .string("")
            .email(localeText?.signUpEnterValidEmail)
            .required(localeText?.signUpEmailRequired),
        createPassword: yup
            .string('')
            .matches(/^(?=.*[0-9])(?=.*[A-Z])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{8,12}$/, localeText?.signUpPasswordMatches)
            .required(localeText?.signUpPasswordRequired),
        confirmPassword: yup
            .string("")
            .matches(/^(?=.*[0-9])(?=.*[A-Z])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{8,12}$/, localeText?.signUpPasswordMatches)
            .required(localeText?.signUpPasswordRequired)
            .oneOf([yup.ref('createPassword'), null], localeText?.signUpPasswordsMustMatch),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            email: "",
            createPassword: "",
            confirmPassword: ""
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            props.setLoader(true)
            dispatch(addStageData({ ...store, ...values }))
            props.handleSubmit("signupSubmit", values)
        },
    });

    return (
        <Grid container item md={6} justifyContent="center" alignItems="center"
            css={{ padding: "3% 7%", paddingLeft: isSmallScreen ? "7%" : "10%" }}
        >
            <form autocomplete="off" onSubmit={formik.handleSubmit} css={{ width: "100%" }}>
                <Grid item css={{ textAlign: "center" }}>
                    <Typography css={{ fontWeight: "bold", lineHeight: "40px", fontSize: "30px", color: "#0098B0" }}>
                        {localeText?.signUpTitle}
                    </Typography>
                </Grid>
                <Grid item xs={12} css={{ marginTop: "20px" }}>
                    <StyledTextField InputLabelProps={{
                        shrink: true,
                    }}
                        name="name" id="name"
                        required
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                        fullWidth label={localeText?.signUpName} variant="outlined"
                    />
                </Grid>
                <Grid item xs={12} css={{ marginTop: "20px" }}>
                    <StyledTextField InputLabelProps={{
                        shrink: true,
                    }}
                        name="email" id="email"
                        value={formik.values.email}
                        required
                        onChange={formik.handleChange}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        fullWidth label={localeText?.signUpEmail} variant="outlined"
                    />
                </Grid>
                <Grid item xs={12} css={{ marginTop: "20px" }}>
                    <StyledTextField InputLabelProps={{
                        shrink: true,
                    }}
                        type={showPassword ? "text" : "password"}
                        name="createPassword" id="createPassword"
                        value={formik.values.createPassword}
                        required
                        onChange={formik.handleChange}
                        error={formik.touched.createPassword && Boolean(formik.errors.createPassword)}
                        helperText={formik.touched.createPassword && formik.errors.createPassword}
                        fullWidth label={localeText?.signUpPassword} variant="outlined"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                    >
                                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Grid>
                <Grid item xs={12} css={{ marginTop: "20px" }}>
                    <StyledTextField InputLabelProps={{
                        shrink: true,
                    }} type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword" id="confirmPassword"
                        value={formik.values.confirmPassword}
                        required
                        onChange={formik.handleChange}
                        error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                        helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                        fullWidth
                        label={localeText?.signUpPasswordConfirm}
                        variant="outlined"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowConfirmPassword}
                                        onMouseDown={handleMouseDownConfirmPassword}
                                    >
                                        {showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Grid>
                <Grid item xs={12} css={{ marginTop: "20px" }}>
                    <Button disabled={props.loader} type="submit" css={{ backgroundColor: "#24B7CC", ":hover": { backgroundColor: "#24B7CC" } }} fullWidth variant="contained" color="primary">
                        {localeText?.signUpProceed}
                    </Button>
                </Grid>
                <Grid item xs={12} css={{ marginTop: "15px", textAlign: "center" }}>
                    <Typography display="inline" variant='caption' >
                        {localeText?.signUpLoginId}
                    </Typography>&nbsp;
                    <Typography display="inline" variant='caption' css={{ textDecoration: "underline", color: "#008FFF" }}>
                        {localeText?.signUpLogin}
                    </Typography>
                </Grid>
            </form>
        </Grid>
    );
}

export default SignUp;